import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../api';
import Register from '../../hr/components/Register';
import { n2 } from '../py';
import { payrollSubUrl } from '../catalog';
import { PayItems } from './Compensation';
import { Select } from '../../hr/components/Controls';
import Loader from '../../hr/components/Loader';

// Stephanet's Deductions & Statutory group: Deduction Management (employee pay items of type deduction), Tax Calculation Engine
// (custom/PayeCalculator.jsx), Statutory Remittances, Pension Tracking and Statutory Schedules (its reports over payroll_items and
// statutory_remittances). The calculator uses the payroll engine's own PAYE rules (Setup Rules), so it agrees with the payroll; Pension
// Tracking counts approved and paid runs only (the user's decision).

export const DeductionManagement = () => <PayItems kind="deductions" />;

// ------------------------------------------------------------------ TAX CALCULATION ENGINE

const INPUTS = [['basic', 'Basic'], ['housing', 'Housing'], ['transport', 'Transport'], ['other', 'Other Allowances'], ['annual_rent', 'Annual Rent Paid']];

export function TaxEngine() {
  const [form, setForm] = useState({ basic: 300000, housing: 75000, transport: 30000, other: 0, annual_rent: 0 });
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const calc = async e => {
    e.preventDefault();
    try { setRes(await api.payeCalculator(form)); setError(''); } catch (err) { setError(err.message); }
  };
  const rows = res && [
    ['Monthly Gross', res.monthly_gross], ['Annual Gross', res.annual_gross], [`Pension (employee, monthly${res.pension_base === 'FULL' ? ', on full pay' : ''})`, res.monthly_pension],
    ['NHF (monthly)', res.monthly_nhf], ['Rent Relief (annual)', res.rent_relief], ['Free Pay (annual)', res.free_pay],
    ['Taxable Income (annual)', res.taxable_income], ['Annual Tax', res.annual_tax], ['Monthly PAYE', res.monthly_paye, true], ['Monthly Net Pay', res.monthly_net, true],
  ];
  return (
    <>
      <div className="alert info" style={{ marginBottom: 12 }}>
        Uses the PAYE bands, pension, NHF and rent relief set in Setup Rules. <Link to={payrollSubUrl('py-setup')}>Open Setup Rules →</Link>
      </div>
      <div className="split">
        <form className="panel side-list" onSubmit={calc}>
          <h3 className="panel-title">Monthly Salary</h3>
          {INPUTS.map(([k, l]) => (
            <label key={k} className="field"><span className="field-label">{l} (₦{k === 'annual_rent' ? ' a year' : ''})</span><input type="number" value={form[k]} onChange={set(k)} /></label>
          ))}
          <button className="btn primary block">Calculate PAYE</button>
          {error && <div className="error">{error}</div>}
          <p className="muted small">Nigeria Tax Act 2025: free pay = pension + NHF + rent relief (no CRA from 2026). Taxable = annual gross − free pay.
            Bands, pension, NHF and rent relief come from Payroll &amp; Compensation › Setup Rules.</p>
        </form>
        <div className="grow">
          {res && (
            <div className="panel">
              <h3 className="panel-title">Breakdown</h3>
              <table><tbody>{rows.map(([l, v, b]) => <tr key={l} className={b ? 'total-row' : ''}><td>{l}</td><td className="num">{n2(v)}</td></tr>)}</tbody></table>
              <h3 className="panel-title" style={{ marginTop: 20 }}>Tax Bands Applied</h3>
              <table>
                <thead><tr><th>Band</th><th className="num">Size</th><th className="num">Rate</th></tr></thead>
                <tbody>{res.bands.map(([size, rate], i) => <tr key={i}><td>{i + 1}</td><td className="num">{size ? n2(size) : 'Remainder'}</td><td className="num">{rate}%</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ PENSION TRACKING (by employee, approved and paid runs)

export function PensionTracking() {
  const [opts, setOpts] = useState({ years: [], runs: [] });
  const [year, setYear] = useState('');
  const [run, setRun] = useState('');
  const load = useCallback(() => api.pensionTracking({ year, run_id: run }).then(r => { setOpts(r); return r.rows; }), [year, run]);
  const res = useMemo(() => ({
    key: 'pension_tracking', label: 'Pension Line', plural: 'Pension Tracking', title: r => `${r.employee_no} ${r.name}`, load,
    search: ['employee_no', 'name', 'pfa', 'rsa_pin'],
    fields: [
      { name: 'employee_no', label: 'Employee ID', list: true }, { name: 'name', label: 'Employee', list: true },
      { name: 'pfa', label: 'PFA', list: true }, { name: 'rsa_pin', label: 'RSA PIN', list: true },
      { name: 'count', label: 'Payroll lines', type: 'integer', list: true },
      { name: 'pension_employee', label: 'Pension (EE)', type: 'money', list: true }, { name: 'pension_employer', label: 'Pension (ER)', type: 'money', list: true },
      { name: 'total', label: 'Total Pension', type: 'money', list: true },
    ],
  }), [load]);
  return (
    <>
      <div className="mdr-card ps-filters no-print" style={{ marginBottom: 12 }}>
        <label className="ps-field"><span>Year</span><Select title="Year" value={year} onChange={e => setYear(e.target.value)}><option value="">All years</option>{opts.years.map(y => <option key={y}>{y}</option>)}</Select></label>
        <label className="ps-field"><span>Payroll Run</span><Select title="Payroll Run" value={run} onChange={e => setRun(e.target.value)}><option value="">All approved runs</option>{opts.runs.map(r => <option key={r.id} value={r.id}>{r.code}</option>)}</Select></label>
        <span className="muted small">Approved and paid payroll runs only.</span>
      </div>
      <Register res={res} canEdit={false} />
    </>
  );
}

// ------------------------------------------------------------------ STATUTORY SCHEDULES (remittances by type)

export function StatutorySchedule() {
  const [opts, setOpts] = useState({ periods: [], statuses: [] });
  const [status, setStatus] = useState('');
  const [period, setPeriod] = useState('');
  const [rows, setRows] = useState(null);
  useEffect(() => { api.statutorySchedule({ status, period }).then(r => { setOpts(r); setRows(r.rows); }).catch(() => setRows([])); }, [status, period]);
  const total = (rows || []).reduce((t, r) => ({ count: t.count + r.count, amount: t.amount + r.amount, due: t.due + r.due, remitted: t.remitted + r.remitted }),
    { count: 0, amount: 0, due: 0, remitted: 0 });
  return (
    <div className="panel">
      <div className="toolbar">
        <label className="ps-field"><span>Status</span><Select title="Status" value={status} onChange={e => setStatus(e.target.value)}><option value="">All</option>{opts.statuses.map(s => <option key={s}>{s}</option>)}</Select></label>
        <label className="ps-field"><span>Period</span><Select title="Period" value={period} onChange={e => setPeriod(e.target.value)}><option value="">All periods</option>{opts.periods.map(p => <option key={p}>{p}</option>)}</Select></label>
        <div className="spacer" />
        <Link className="btn ghost" to={payrollSubUrl('statutory')}>Open Statutory Remittances →</Link>
      </div>
      <div className="table-wrap"><table className="register-table">
        <thead><tr><th>Type</th><th>Authority / PFA</th><th className="num">Count</th><th className="num">Amount</th><th className="num">Due</th><th className="num">Remitted</th></tr></thead>
        <tbody>
          {rows === null && <tr><td colSpan={6}><Loader title="Loading…" /></td></tr>}
          {rows && !rows.length && <tr><td colSpan={6} className="empty">No remittances yet — they are raised when a payroll run is approved.</td></tr>}
          {(rows || []).map(r => <tr key={r.id}><td><b>{r.statutory_type}</b></td><td>{r.authority}</td><td className="num">{r.count}</td><td className="num">{n2(r.amount)}</td>
            <td className="num">{n2(r.due)}</td><td className="num">{n2(r.remitted)}</td></tr>)}
        </tbody>
        {rows && rows.length > 0 && <tfoot><tr><td colSpan={2}><b>Total</b></td><td className="num">{total.count}</td><td className="num">{n2(total.amount)}</td>
          <td className="num">{n2(total.due)}</td><td className="num">{n2(total.remitted)}</td></tr></tfoot>}
      </table></div>
    </div>
  );
}
